import { collection, query, where, getDocs, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { BaseCRUDManager } from '../base/BaseCRUDManager';
import { ProjectManager } from '../ProjectManager';
import { isDemoMode } from '../../utilities/demoMode';
import { DemoDataManager } from '../../services/demoDataManager';
import RecordingStore from '../../stores/RecordingStore';
import { IProject, IRecording, IRecordingWithAuthor } from '../../types';
import { createRecordingWithAuthor } from './typeUtils';
import { attachAuthorsToEntities } from '../base/authorAttachment';

const RECORDINGS_COLLECTION = 'Recordings';

/**
 * Get all recordings
 */
export async function getAllRecordings(): Promise<IRecording[]> {
  try {
    return await BaseCRUDManager.getAll<IRecording>(
      RECORDINGS_COLLECTION,
      { idField: 'key' }
    );
  } catch (error) {
    console.error('Error fetching all recordings:', error);
    return [];
  }
}

/**
 * Get recordings for a specific pin
 */
export async function getRecordingsForPin(pinKey: string): Promise<IRecordingWithAuthor[]> {
  if (isDemoMode()) {
    const recordings = DemoDataManager.getRecordingsForPin(pinKey);
    return recordings.map(rec => ({ ...rec, author: undefined })) as IRecordingWithAuthor[];
  }

  try {
    const recordings = await BaseCRUDManager.query<IRecording>(
      RECORDINGS_COLLECTION,
      {
        filters: [{ field: 'pinKey', operator: '==', value: pinKey }]
      },
      { idField: 'key' }
    );

    return attachAuthorsToEntities(recordings);
  } catch (error) {
    console.error('Error fetching recordings for pin:', error);
    return [];
  }
}

/**
 * Get recordings by project key
 */
export async function getRecordingsByProjectKey(
  project: string,
  includeDemo: boolean = false
): Promise<IRecording[]> {
  if (isDemoMode()) {
    const recordings = DemoDataManager.getRecordings(project);
    return recordings;
  }

  try {
    const recordings = await BaseCRUDManager.query<IRecording>(
      RECORDINGS_COLLECTION,
      {
        filters: [{ field: 'project', operator: '==', value: project }]
      },
      { idField: 'key' }
    );

    // Filter out demo recordings if includeDemo is false
    if (!includeDemo) {
      return recordings.filter(recording => !recording.isDemo);
    }

    return recordings;
  } catch (error) {
    console.error('Error fetching recordings by project key:', error);
    return [];
  }
}

/**
 * Get recordings by album key
 */
export async function getRecordingsByAlbumKey(albumKey: string): Promise<IRecording[]> {
  try {
    return await BaseCRUDManager.query<IRecording>(
      RECORDINGS_COLLECTION,
      {
        filters: [{ field: 'albumKey', operator: '==', value: albumKey }]
      },
      { idField: 'key' }
    );
  } catch (error) {
    console.error(`Error fetching recordings with albumKey: ${albumKey}:`, error);
    return [];
  }
}

/**
 * Get a single recording by album key
 */
export async function getRecordingByAlbumKey(albumKey: string): Promise<IRecording | null> {
  try {
    const recordings = await getRecordingsByAlbumKey(albumKey);
    return recordings.length > 0 ? recordings[0] : null;
  } catch (error) {
    console.error('Error fetching recording by album key:', error);
    return null;
  }
}

/** Orphaned = no pin assignment, excluding the optional project intro recording. */
export function filterOrphanedRecordingsForProject(
  recordings: IRecording[],
  introRecordingKey?: string | null
): IRecording[] {
  return recordings.filter((recording) => {
    if (introRecordingKey && recording.key === introRecordingKey) {
      return false;
    }
    return !recording.pinKey || recording.pinKey === '';
  });
}

async function getIntroRecordingKeyForProject(projectKey: string): Promise<string | undefined> {
  if (isDemoMode()) {
    try {
      const stored = localStorage.getItem(`demo_project_settings_${projectKey}`);
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<IProject>;
        return parsed.settings?.introRecordingKey;
      }
    } catch {
      // Ignore malformed demo settings
    }
    return undefined;
  }

  try {
    const project = await ProjectManager.getProject(projectKey);
    return project?.settings?.introRecordingKey;
  } catch {
    return undefined;
  }
}

/**
 * Get orphaned recordings for a project (recordings without pinKey)
 */
export async function getOrphanedRecordingsForProject(projectKey: string): Promise<IRecording[]> {
  const introRecordingKey = await getIntroRecordingKeyForProject(projectKey);

  if (isDemoMode()) {
    const recordings = DemoDataManager.getOrphanedRecordingsForProject(projectKey);
    return filterOrphanedRecordingsForProject(recordings, introRecordingKey);
  }

  try {
    const recordings = await getRecordingsByProjectKey(projectKey);
    return filterOrphanedRecordingsForProject(recordings, introRecordingKey);
  } catch (error) {
    console.error(`Error fetching orphaned recordings for project ${projectKey}:`, error);
    return [];
  }
}

/**
 * Get recordings by author key
 */
export async function getRecordingsByAuthorKey(authorKey: string): Promise<IRecording[]> {
  try {
    return await BaseCRUDManager.query<IRecording>(
      RECORDINGS_COLLECTION,
      {
        filters: [{ field: 'ownership.recordingAuthor', operator: '==', value: authorKey }]
      },
      { idField: 'key' }
    );
  } catch (error) {
    console.error('Error fetching recordings for author:', error);
    return [];
  }
}

/**
 * Update recordings order in a project
 */
export async function updateRecordingsOrder(
  projectId: string,
  orderedRecordings: { key: string; order: number }[]
): Promise<void> {
  const batch = writeBatch(db);
  
  orderedRecordings.forEach(({ key, order }) => {
    const recordingRef = doc(db, RECORDINGS_COLLECTION, key);
    batch.update(recordingRef, { collectionOrder: order });
  });

  await batch.commit();
}

/**
 * Update episode recording play order (sequenceNumber is what the mobile app uses).
 */
export async function updateEpisodeRecordingsSequence(
  orderedRecordings: { key: string; sequenceNumber: number }[]
): Promise<void> {
  if (isDemoMode()) {
    orderedRecordings.forEach(({ key, sequenceNumber }) => {
      DemoDataManager.updateRecording(key, { sequenceNumber });
    });
    return;
  }

  const batch = writeBatch(db);

  orderedRecordings.forEach(({ key, sequenceNumber }) => {
    const recordingRef = doc(db, RECORDINGS_COLLECTION, key);
    batch.update(recordingRef, { sequenceNumber });
  });

  await batch.commit();
}

/**
 * Update recordings author (batch update)
 */
export async function updateRecordingsAuthor(
  oldAuthorKeys: string[],
  newAuthorKey: string
): Promise<void> {
  try {
    const recordings = await BaseCRUDManager.query<IRecording>(
      RECORDINGS_COLLECTION,
      {
        filters: [{ field: 'ownership.recordingAuthor', operator: 'in', value: oldAuthorKeys }]
      },
      { idField: 'key' }
    );

    const batch = writeBatch(db);
    recordings.forEach(recording => {
      const recordingRef = doc(db, RECORDINGS_COLLECTION, recording.key);
      batch.update(recordingRef, { 'ownership.recordingAuthor': newAuthorKey });
    });

    await batch.commit();
  } catch (error) {
    console.error('Error updating recordings author:', error);
    throw error;
  }
}

/**
 * Fetch recordings for episode project with authors
 */
export async function fetchRecordingsForEpisodeProject(
  projectId: string,
  includeDemo: boolean = true
): Promise<IRecordingWithAuthor[]> {
  const recordings = await getRecordingsByProjectKey(projectId, includeDemo);
  
  // In demo mode, check RecordingStore first for recordings with author data
  if (isDemoMode()) {
    const recordingsWithAuthors: IRecordingWithAuthor[] = recordings.map(recording => {
      const storedRecording = RecordingStore.getRecording(recording.key);
      if (storedRecording && storedRecording.author) {
        return storedRecording;
      }
      return { ...recording, author: undefined } as IRecordingWithAuthor;
    });
    return recordingsWithAuthors;
  }
  
  // In regular mode, fetch authors for each recording
  return attachAuthorsToEntities(recordings) as Promise<IRecordingWithAuthor[]>;
}
